import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import * as projectTemplatesApi from 'api/projectTemplates/projectTemplates.api';
import type { IState } from 'redux/state';

export interface ProjectTemplate {
  id: number;
  name: string;
  description?: string | null;
  auto_create_on_new_client: boolean;
  add_all_users: boolean;
  color?: string | null;
  rate_type?: string | null;
  hour_rate?: number | null;
  required_notes: boolean;
  enable_labels?: string | null;
  required_labels: boolean;
  label_ids: number[];
  required_label_ids: number[];
  allow_only_one_tag: boolean;
  trigger_source?: string | null;
  updated_at: string;
}

export interface ProjectTemplatesState {
  templates: Array<ProjectTemplate>;
  hasLoaded: boolean;
  hasLoadFailed: boolean;
}

const initialState: ProjectTemplatesState = {
  templates: [],
  hasLoaded: false,
  hasLoadFailed: false,
};

const projectTemplatesSlice = createSlice({
  name: 'projectTemplates',
  initialState,
  reducers: {
    setTemplates: (state, action: PayloadAction<Array<ProjectTemplate>>) => {
      state.templates = action.payload;
    },
    upsertTemplate: (state, action: PayloadAction<ProjectTemplate>) => {
      const index = state.templates.findIndex(t => t.id === action.payload.id);
      if (index >= 0) {
        state.templates[index] = action.payload;
      } else {
        state.templates.push(action.payload);
      }
    },
    removeTemplate: (state, action: PayloadAction<number>) => {
      state.templates = state.templates.filter(t => t.id !== action.payload);
    },
    finishLoading: state => {
      state.hasLoaded = true;
      state.hasLoadFailed = false;
    },
    failLoading: state => {
      state.hasLoaded = true;
      state.hasLoadFailed = true;
    },
  },
});

const readProjectTemplatesState = (state: IState) => state.projectTemplates;

export const getProjectTemplates = createSelector(
  [readProjectTemplatesState],
  projectTemplates => projectTemplates.templates
);

export const getHasLoadedProjectTemplates = createSelector(
  [readProjectTemplatesState],
  projectTemplates => projectTemplates.hasLoaded
);

export const getHasProjectTemplatesLoadFailed = createSelector(
  [readProjectTemplatesState],
  projectTemplates => projectTemplates.hasLoadFailed
);

export const fetchProjectTemplates = createAsyncThunk(
  'projectTemplates/fetch',
  async (_, thunkAPI) => {
    try {
      const templates = await projectTemplatesApi.getProjectTemplates();
      thunkAPI.dispatch(projectTemplatesSlice.actions.setTemplates(templates));
      thunkAPI.dispatch(projectTemplatesSlice.actions.finishLoading());
    } catch (error) {
      thunkAPI.dispatch(projectTemplatesSlice.actions.failLoading());
      throw error;
    }
  }
);

export const saveProjectTemplate = createAsyncThunk(
  'projectTemplates/save',
  async (template: Partial<ProjectTemplate>, thunkAPI) => {
    const { id, updated_at: _, ...payload } = template;
    const saved = id
      ? await projectTemplatesApi.updateProjectTemplate(id, payload)
      : await projectTemplatesApi.createProjectTemplate(
          payload as { name: string } & typeof payload
        );

    thunkAPI.dispatch(projectTemplatesSlice.actions.upsertTemplate(saved));
  }
);

export const deleteProjectTemplate = createAsyncThunk(
  'projectTemplates/delete',
  async (id: number, thunkAPI) => {
    await projectTemplatesApi.deleteProjectTemplate(id);
    thunkAPI.dispatch(projectTemplatesSlice.actions.removeTemplate(id));
  }
);

export default projectTemplatesSlice.reducer;
